import { useState, useEffect } from "react";
import {
  Progress,
  Avatar,
  Modal,
  Input,
  Select,
  Upload,
  Button,
  DatePicker,
  notification,
} from "antd";
import dayjs from "dayjs";
import {
  EyeOutlined,
  DollarOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import GenericStatCard from "../../../../Components/Cards/GenericStatCard";
import garageMoneyIcon from "../../../../Assets/Icons/garage_money.svg";
import laptopCarIcon from "../../../../Assets/Icons/laptop_car.svg";
import { useNavigate, useLocation } from "react-router-dom";
import GenericTable from "../../../../Components/Tables/GenericTable";
import {
  getManagerCredits,
  submitCreditTopUpRequest,
  resubmitCreditTopUpRequest,
  initiateWiPayTopUp,
} from "../../../../API/FleetManager/CreditsBillings";
import { savePaymentReturn } from "../../../../Utlis/PaymentReturn";
import OpenRequestBanner, {
  getOpenRequestState,
} from "../../../../Components/OpenRequestBanner";
import "./style.css";
import { getInitials } from "../../../../Components/InitialsAvatar";

const AVATAR_COLORS = [
  "#4F46E5",
  "#EA580C",
  "#16A34A",
  "#DC2626",
  "#2563EB",
  "#7C3AED",
];
const getAvatarColor = (name) =>
  AVATAR_COLORS[(name || "A").charCodeAt(0) % AVATAR_COLORS.length];

const BADGE_COLORS = [
  { background: "#4f46e51f", color: "#4f46e5" },
  { background: "#fee2e2", color: "#dc2626" },
  { background: "#ecfdf5", color: "#059669" },
  { background: "#ede9fe", color: "#7c3aed" },
  { background: "#f59e0b1f", color: "#f59e0b" },
  { background: "#dbeafe", color: "#3b82f6" },
  { background: "#ea580c1a", color: "#ea580c" },
  { background: "#fdf2f8", color: "#db2777" },
  { background: "#f0fdfa", color: "#0d9488" },
];

const hashString = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
};

const getModuleBadgeStyle = (name) => {
  if (!name || name === "-") {
    return { background: "#f4f4f5", color: "#71717a" };
  }
  if (name.toLowerCase() === "fuel") {
    return { background: "#0000000f", color: "#000000" };
  }
  return BADGE_COLORS[hashString(name) % BADGE_COLORS.length];
};

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar)) return avatar;
  return `${BACKEND_ORIGIN}${avatar.startsWith("/") ? "" : "/"}${avatar}`;
};

const formatCurrency = (value) => `$${(parseFloat(value) || 0).toFixed(2)}`;

const AvatarWithFallback = ({ src, name }) =>
  src ? (
    <Avatar src={src} size="small" style={{ objectFit: "cover" }} />
  ) : (
    <Avatar
      size="small"
      style={{
        background: getAvatarColor(name),
        fontSize: 12,
        fontWeight: 700,
      }}
    >
      {getInitials(name)}
    </Avatar>
  );

const formatApiError = (data, fallback) => {
  if (!data || typeof data !== "object") return fallback;
  if (typeof data.detail === "string") return data.detail;
  if (typeof data.message === "string" && data.message) return data.message;

  const messages = Object.entries(data)
    .filter(([key]) => key !== "data")
    .flatMap(([key, value]) => {
      const label =
        key === "non_field_errors"
          ? ""
          : `${key.charAt(0).toUpperCase()}${key.slice(1).replace(/_/g, " ")}: `;
      return []
        .concat(value)
        .filter((msg) => typeof msg === "string")
        .map((msg) => `${label}${msg}`);
    });

  return messages.length ? messages.join(" ") : fallback;
};

const getFileName = (url) => {
  const last = String(url || "").split("?")[0].split("/").pop();
  try {
    return decodeURIComponent(last) || "View file";
  } catch {
    return last || "View file";
  }
};

const PayFleetModal = ({
  open,
  onClose,
  onConfirm,
  onWiPay,
  availableCredits,
  hideWiPay,
  initialValues,
  existingProofUrl,
}) => {
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [paymentDate, setPaymentDate] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [wipayLoading, setWipayLoading] = useState(false);

  const handleWiPay = async () => {
    const numericAmount = Number(amount);

    if (!amount || isNaN(numericAmount) || numericAmount <= 0) {
      notification.error({
        message: "Amount Required",
        description: "Please enter a valid top up amount.",
        placement: "topRight",
      });
      return;
    }

    setWipayLoading(true);
    const success = await onWiPay(amount);
    setWipayLoading(false);
    if (success) onClose();
  };

  useEffect(() => {
    if (!open) {
      setAmount("");
      setPaymentMethod(null);
      setPaymentDate(null);
      setFileList([]);
      return;
    }
    if (initialValues) {
      setAmount(
        initialValues.amount != null ? String(initialValues.amount) : "",
      );
      setPaymentMethod(initialValues.paymentMethod || null);
      const date = initialValues.paymentDate
        ? dayjs(initialValues.paymentDate)
        : null;
      setPaymentDate(date && date.isValid() ? date : null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleConfirm = () => {
    const numericAmount = Number(amount);

    if (!amount || isNaN(numericAmount) || numericAmount <= 0) {
      notification.error({
        message: "Amount Required",
        description: "Please enter a valid top up amount.",
        placement: "topRight",
      });
      return;
    }

    if (!paymentMethod) {
      notification.error({
        message: "Payment Method Required",
        description: "Please select a payment method.",
        placement: "topRight",
      });
      return;
    }

    if (!paymentDate) {
      notification.error({
        message: "Payment Date Required",
        description: "Please select the payment date.",
        placement: "topRight",
      });
      return;
    }

    if ((!fileList || fileList.length === 0) && !existingProofUrl) {
      notification.error({
        message: "Upload Required",
        description: "Please upload transaction screenshot before submitting.",
        placement: "topRight",
      });
      return;
    }

    onConfirm({
      amount,
      paymentMethod,
      paymentDate: paymentDate.format("YYYY-MM-DD"),
      fileList,
    });
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={null}
      width={520}
      className="pay-fleet-modal"
      centered
    >
      <div className="pay-fleet-modal__header">
        <div className="pay-fleet-modal__title">Top Up</div>
        <div className="pay-fleet-modal__subtitle">
          Choose payment option to top up your fleet credit balance
        </div>
      </div>

      <div className="pay-fleet-modal__balance-card">
        <div className="pay-fleet-modal__balance-info">
          <div className="pay-fleet-modal__balance-avatar">FL</div>
          <div>
            <div className="pay-fleet-modal__balance-name">
              Fleet Credit Account
            </div>
            <div className="pay-fleet-modal__balance-id">ACCOUNT: FLT-9982</div>
          </div>
        </div>
        <div className="pay-fleet-modal__due">
          <div className="pay-fleet-modal__due-label">Available Credits</div>
          <div className="pay-fleet-modal__due-amount">
            ${availableCredits.toLocaleString()}
          </div>
        </div>
      </div>

      <div className="pay-fleet-modal__field">
        <label className="pay-fleet-modal__label">
          Top Up Amount <span className="pay-fleet-modal__required">*</span>
        </label>
        <Input
          prefix="$"
          value={amount}
          onChange={(e) => {
            const val = e.target.value.replace(/[^0-9.]/g, "");
            const parts = val.split(".");
            setAmount(
              parts.length > 2 ? `${parts[0]}.${parts.slice(1).join("")}` : val,
            );
          }}
          placeholder="0.00"
          className="pay-fleet-modal__input"
        />
      </div>

      <div className="pay-fleet-modal__field">
        <label className="pay-fleet-modal__label">
          Payment Method
        </label>
        <Select
          className="pay-fleet-modal__select"
          value={paymentMethod}
          onChange={(value) => {
            setPaymentMethod(value ?? null);
            if (!value) setPaymentDate(null);
          }}
          placeholder="Select payment method"
          options={[
            { value: "bank_transfer", label: "Bank Transfer" },
            { value: "cash", label: "Cash" },
          ]}
        />
      </div>

      <div className="pay-fleet-modal__field">
        <label className="pay-fleet-modal__label">
          Payment Date
        </label>
        <DatePicker
          className="pay-fleet-modal__input"
          style={{ width: "100%" }}
          value={paymentDate}
          onChange={setPaymentDate}
          disabled={!paymentMethod}
          placeholder={paymentMethod ? "Select date" : "Select payment method first"}
          format="YYYY-MM-DD"
          disabledDate={(current) => current && current > dayjs().endOf("day")}
        />
      </div>

      <div className="pay-fleet-modal__field">
        <label className="pay-fleet-modal__label">
          Upload Transaction Screenshot
        </label>
        <Upload.Dragger
          fileList={fileList}
          onChange={({ fileList: fl }) => setFileList(fl)}
          beforeUpload={() => false}
          accept=".png,.jpg,.jpeg,.pdf"
          className="pay-fleet-modal__dragger"
        >
          <p className="pay-fleet-modal__dragger-text">
            Choose a file or drag & drop it here
          </p>
          <p className="pay-fleet-modal__dragger-hint">
            PNG, PDF and JPG formats, up to 5MB
          </p>
          <Button
            icon={<UploadOutlined />}
            size="small"
            className="pay-fleet-modal__browse-btn"
          >
            Browse File
          </Button>
        </Upload.Dragger>
        {existingProofUrl && fileList.length === 0 && (
          <div className="existing-proof-note">
            <span>Current proof:</span>
            <a
              href={existingProofUrl}
              target="_blank"
              rel="noreferrer"
              title={getFileName(existingProofUrl)}
            >
              {getFileName(existingProofUrl)}
            </a>
          </div>
        )}
      </div>

      <div className="pay-fleet-modal__footer">
        {!hideWiPay && (
          <Button
            className="pay-fleet-modal__wipay-btn"
            onClick={handleWiPay}
            loading={wipayLoading}
          >
            Pay through WiPay
          </Button>
        )}
        <Button
          type="primary"
          className="pay-fleet-modal__confirm-btn"
          onClick={handleConfirm}
        >
          Confirm Payment
        </Button>
      </div>
    </Modal>
  );
};

const Dashboard = ({ setBtnConfigs }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isPayModalOpen, setIsPayModalOpen] = useState(false);
  const [managerCredits, setManagerCredits] = useState(null);

  const fetchManagerCredits = async () => {
    try {
      const res = await getManagerCredits();

      if (Math.floor(res?.status / 100) === 2) {
        setManagerCredits(res?.data?.data || res?.data || null);
      }
    } catch (err) {
      console.error("Failed to load manager credits:", err);
    }
  };

  useEffect(() => {
    fetchManagerCredits();
  }, []);

  const used = parseFloat(managerCredits?.allocated_total) || 0;
  const limit =
    parseFloat(managerCredits?.total_balance?.value ?? managerCredits?.total_balance) ||
    0;
  const available = parseFloat(managerCredits?.unallocated_balance) || 0;

  const openRequest = managerCredits?.open_request;
  const topUpFallbackMessage =
    "You already have a top-up request under review. You can send a new one once it has been reviewed.";
  const {
    canCreate: canCreateRequest,
    message: blockedMessage,
    resubmitId,
  } = getOpenRequestState(openRequest, topUpFallbackMessage);
  const canOpenTopUp = canCreateRequest || resubmitId != null;
  const resubmitRequest = resubmitId != null ? openRequest?.request : null;
  const percentageUsed = limit ? (used / limit) * 100 : 0;

  const handleView = (record) => {
    navigate("/credit-adjustments-manager", {
      state: { from: "Dashboard", vehicleId: record.key },
    });
  };

  const handleConfirmPayment = async ({
    amount,
    paymentMethod,
    paymentDate,
    fileList,
  }) => {
    try {
      const formData = new FormData();
      formData.append("amount", amount);
      formData.append("payment_method", paymentMethod);
      formData.append("payment_date", paymentDate);
      if (fileList[0]?.originFileObj) {
        formData.append("proof_file", fileList[0].originFileObj);
      }

      const res =
        resubmitId != null
          ? await resubmitCreditTopUpRequest(resubmitId, formData)
          : await submitCreditTopUpRequest(formData);

      if (Math.floor(res?.status / 100) === 2) {
        fetchManagerCredits();

        notification.success({
          message:
            resubmitId != null ? "Request Resubmitted" : "Payment Confirmed",
          description:
            resubmitId != null
              ? `Your top-up request of $${amount} has been resubmitted for review.`
              : `Payment of $${amount} has been submitted.`,
          placement: "topRight",
        });
      } else {
        notification.error({
          message: "Payment Failed",
          description:
            formatApiError(res?.data, "Failed to submit top-up request."),
          placement: "topRight",
        });
      }
    } catch (err) {
      notification.error({
        message: "Payment Failed",
        description:
          formatApiError(err?.response?.data, "Failed to submit top-up request."),
        placement: "topRight",
      });
    }
  };

  const handleWiPayPayment = async (amount) => {
    try {
      const res = await initiateWiPayTopUp({
        amount,
        return_url: `${window.location.origin}/credits-billings-manager`,
      });

      if (Math.floor(res?.status / 100) === 2) {
        const checkoutUrl = res?.data?.data?.checkout_url;

        if (checkoutUrl) {
          notification.success({
            message: "Redirecting to WiPay",
            description:
              res?.data?.message || "WiPay checkout initiated successfully",
            placement: "topRight",
          });
          savePaymentReturn(location);
          window.location.href = checkoutUrl;
          return true;
        }

        notification.error({
          message: "WiPay Payment Failed",
          description: "WiPay checkout URL was not returned.",
          placement: "topRight",
        });
        return false;
      }

      notification.error({
        message: "WiPay Payment Failed",
        description: formatApiError(res?.data, "Failed to initiate WiPay payment."),
        placement: "topRight",
      });
      return false;
    } catch (err) {
      notification.error({
        message: "WiPay Payment Failed",
        description:
          formatApiError(err?.response?.data, "Failed to initiate WiPay payment."),
        placement: "topRight",
      });
      return false;
    }
  };

  useEffect(() => {
    setBtnConfigs?.([
      {
        text: resubmitId != null ? "Resubmit Top Up" : "Top Up",
        icon: <DollarOutlined />,
        disabled: !canOpenTopUp,
        title: canOpenTopUp ? undefined : blockedMessage,
        onClick: () => {
          if (!canOpenTopUp) {
            notification.warning({
              message: "Top-up request pending",
              description: blockedMessage,
              placement: "topRight",
            });
            return;
          }
          setIsPayModalOpen(true);
        },
      },
    ]);
  }, [setBtnConfigs, canOpenTopUp, resubmitId, blockedMessage]);

  const creditsData = (managerCredits?.vehicle_allocations || []).map(
    (allocation) => ({
      key: String(allocation.vehicle_id),
      vehicle: allocation.vehicle || "-",
      plateNumber: allocation.license_plate || "-",
      driverName: allocation.driver || "-",
      driverAvatar: resolveAvatarUrl(allocation.driver_avatar),
      module: Array.isArray(allocation.assigned_modules)
        ? allocation.assigned_modules
        : allocation.assigned_modules
          ? [allocation.assigned_modules]
          : [],
      usage: allocation.usage_percent || 0,
      limit: formatCurrency(allocation.amount),
      status: allocation.status || "-",
    }),
  );

  const moduleFilters = [
    ...new Set(creditsData.flatMap((item) => item.module)),
  ].map((module) => ({ text: module, value: module }));

  const creditsColumns = [
    {
      title: "Vehicle",
      dataIndex: "vehicle",
      key: "vehicle",
      enableSearch: "true",
      render: (text) => (
        <span style={{ fontWeight: 700, color: "#111827" }}>{text}</span>
      ),
    },
    {
      title: "Plate Number",
      dataIndex: "plateNumber",
      key: "plateNumber",
      enableSearch: "true",
    },
    {
      title: "Driver",
      dataIndex: "driver",
      key: "driver",
      enableSearch: "true",
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <AvatarWithFallback
            src={record.driverAvatar}
            name={record.driverName}
          />
          <span style={{ fontSize: "14px", color: "#3F3F46" }}>
            {record.driverName}
          </span>
        </div>
      ),
    },
    {
      title: "Assign Module",
      dataIndex: "module",
      key: "module",
      filters: moduleFilters,
      onFilter: (value, record) => record.module?.includes(value),
      render: (modules) =>
        Array.isArray(modules) && modules.length > 0 ? (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {modules.map((module) => (
              <span
                key={module}
                className="assigned-module-badge"
                style={getModuleBadgeStyle(module)}
              >
                {module}
              </span>
            ))}
          </div>
        ) : (
          "-"
        ),
    },
    {
      title: "Credit Usage",
      dataIndex: "usage",
      key: "usage",
      render: (percent) => {
        let color = "#16A34A";
        if (percent >= 80 && percent < 100) color = "#F59E0B";
        if (percent >= 100) color = "#DC2626";

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "7px",
              width: "160px",
            }}
          >
            <Progress
              percent={percent}
              strokeColor={color}
              showInfo={false}
              size="small"
            />
            <span
              style={{ fontSize: "12px", color: "#71717A", fontWeight: "500" }}
            >
              {percent}%
            </span>
          </div>
        );
      },
      sorter: (a, b) => a.usage - b.usage,
    },
    {
      title: "Limit",
      dataIndex: "limit",
      key: "limit",
      render: (val) => (
        <span style={{ color: "#4F46E5", fontWeight: 500 }}>{val}</span>
      ),
      sorter: (a, b) =>
        parseFloat(String(a.limit).replace(/[^0-9.-]+/g, "")) -
        parseFloat(String(b.limit).replace(/[^0-9.-]+/g, "")),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: [...new Set(creditsData.map((item) => item.status))].map(
        (status) => ({
          text: status,
          value: status,
        }),
      ),
      onFilter: (value, record) => record.status === value,
      render: (text) => (
        <span
          className={`status-badge ${text === "Active" ? "active" : "locked"}`}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <EyeOutlined
            style={{
              cursor: "pointer",
              color: "#4F46E5",
              fontSize: 18,
            }}
            onClick={() => handleView(record)}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="credits-billings-page">
      <OpenRequestBanner
        openRequest={openRequest}
        label="Top-up request"
        fallbackMessage={topUpFallbackMessage}
      />
      <div className="row">
        <div className="stats-container">
          <GenericStatCard
            title="Available to Allocate"
            value={formatCurrency(available)}
            icon={garageMoneyIcon}
          />
          <GenericStatCard
            title="Total Active Vehicles"
            value={managerCredits?.vehicle_count?.value ?? 0}
            {...(managerCredits?.vehicle_count?.percentage != null && {
              percentage: `${managerCredits.vehicle_count.percentage}`,
            })}
            isIncrease={managerCredits?.vehicle_count?.is_increase}
            icon={laptopCarIcon}
          />
        </div>
      </div>
      <div className="row credit-row">
        <div className="tabs card">
          <div className="credit-card">
            <div className="credit-title">Credit Utilization in JMD</div>

            <div className="credit-bar-section">
              <span className="label-card">Available Credits</span>
              <span className="available-amount">
                ${available.toLocaleString()}
              </span>
            </div>

            <div className="credit-bar">
              <div
                className="credit-bar-fill"
                style={{ width: `${percentageUsed}%` }}
              ></div>
            </div>

            <div className="credit-footer">
              <span>
                Used:<span> ${used.toLocaleString()}</span>
              </span>
              <span>
                Limit: <span>${limit.toLocaleString()}</span>
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="credit-table">
        <GenericTable
          dataSource={creditsData}
          columns={creditsColumns}
          enableRowSelection={false}
        />
      </div>
      <PayFleetModal
        open={isPayModalOpen}
        onClose={() => setIsPayModalOpen(false)}
        onConfirm={handleConfirmPayment}
        onWiPay={handleWiPayPayment}
        availableCredits={available}
        hideWiPay={resubmitId != null}
        initialValues={
          resubmitRequest
            ? {
                amount: resubmitRequest.amount,
                paymentMethod: resubmitRequest.payment_method,
                paymentDate: resubmitRequest.payment_date,
              }
            : null
        }
        existingProofUrl={
          resubmitRequest?.proof_file
            ? resolveAvatarUrl(resubmitRequest.proof_file)
            : null
        }
      />
    </div>
  );
};

export default Dashboard;
